import React from 'react';
import { APP_VERSION } from '../config';

/**
 * App-wide error boundary. Catches any uncaught render-time error in the
 * route tree (e.g., a TDZ ReferenceError, undefined property access, hook
 * misorder) and renders a friendly fallback instead of a blank page.
 *
 * Why we need this:
 *   v2.9.8 shipped a CreateSong useEffect that referenced state before its
 *   useState declaration -> ReferenceError ("Cannot access 'coverMode' before
 *   initialization") -> entire page rendered blank with no visible message.
 *   Took us several days and a screenshot to even spot the issue. With this
 *   boundary in place, the user would have seen an actionable fallback
 *   immediately and we'd have caught it on the first deploy.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to console so we can grab it from DevTools when users report.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info?.componentStack);

    // Best-effort: log to backend so admins can browse error history. Never
    // throw from this — if logging itself fails, we just lose that one record.
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL || '';
      const payload = {
        message: error?.message || String(error || 'Unknown error'),
        stack: error?.stack || '',
        component_stack: info?.componentStack || '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        version: APP_VERSION || '',
      };
      const token = (typeof localStorage !== 'undefined' && localStorage.getItem('wim_session_token')) || '';
      fetch(`${apiBase}/api/client-errors`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      }).catch(() => {});
    } catch (_) {
      // swallow — never let error logging error
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const msg = this.state.error?.message || 'Something went wrong.';

    return (
      <div
        className="min-h-[70vh] flex items-center justify-center px-6"
        data-testid="error-boundary-fallback"
      >
        <div className="glass-card p-8 max-w-lg w-full text-center">
          <div className="text-5xl mb-4" aria-hidden="true">😬</div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Something went sideways
          </h1>
          <p className="text-zinc-400 text-sm mb-6">
            This page crashed before it could render. The team has been
            notified and we'll get it sorted. In the meantime, try refreshing
            or head back to your dashboard.
          </p>

          <details className="text-left text-xs text-zinc-500 bg-black/30 rounded-md p-3 mb-6">
            <summary className="cursor-pointer text-zinc-400">
              Technical details
            </summary>
            <pre className="mt-2 whitespace-pre-wrap break-words font-mono">
              {msg}
            </pre>
          </details>

          <div className="flex gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="btn-primary px-5 py-2.5 rounded-md"
              data-testid="error-boundary-reload-btn"
            >
              Refresh page
            </button>
            <button
              onClick={this.handleHome}
              className="px-5 py-2.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-white/5"
              data-testid="error-boundary-home-btn"
            >
              Go to dashboard
            </button>
          </div>

          <p className="text-xs text-zinc-600 mt-6">
            If this keeps happening,{' '}
            <a
              href="mailto:support@wrappedinmusic.com"
              className="text-emerald-400 hover:underline"
            >
              email support@wrappedinmusic.com
            </a>{' '}
            with what you were doing.
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
